import React, { useEffect, useState } from "react";
import { Gift, Loader2, Building2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STATUSES = ["pending", "contacted", "signed", "rewarded", "rejected"];
const STATUS_TINT = {
  pending: "bg-white/5 text-white/55 border-white/10",
  contacted: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  signed: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  rewarded: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function AdminReferrals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const url = filter ? `/admin/referrals?status=${filter}` : "/admin/referrals";
    try {
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch {
      toast.error("Could not load referrals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/referrals/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-referrals-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB000] font-medium mb-2">Pipeline</div>
        <h1 className="text-3xl font-bold tracking-tight">Client referrals</h1>
        <p className="text-sm text-white/50 mt-2">Each closed referral = 1 free month for the referring client. Mark as <strong className="text-emerald-300">rewarded</strong> after the deal closes to credit them.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip active={!filter} onClick={() => setFilter("")}>All</Chip>
        {STATUSES.map((s) => (
          <Chip key={s} active={filter === s} onClick={() => setFilter(filter === s ? "" : s)}>{s}</Chip>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 py-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-12 text-center">
          <Gift className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-white/70 font-medium">No referrals yet</div>
          <div className="text-white/40 text-sm mt-1">Clients can refer new businesses from their portal dashboard.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((r) => (
            <div key={r.id} className="bg-[#121214] border border-white/5 rounded-xl p-5" data-testid={`referral-${r.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-xl bg-[#FFB000]/10 border border-[#FFB000]/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-[#FFB000]" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-base font-semibold text-white">{r.referee_company}</div>
                    <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border font-medium ${STATUS_TINT[r.status] || STATUS_TINT.pending}`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="text-[12px] text-white/45 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{r.referee_contact_name}</span>
                    <span>·</span>
                    <a href={`mailto:${r.referee_email}`} className="text-[#FFB000] hover:underline flex items-center gap-1">{r.referee_email} <ExternalLink className="w-3 h-3" /></a>
                    {r.referee_phone && <><span>·</span><span>{r.referee_phone}</span></>}
                  </div>
                  <div className="text-[12px] text-white/55 mt-2.5">
                    Referred by <strong className="text-white/85">{r.referrer_name || r.referrer_email}</strong> at <strong className="text-white/85">{r.client_name || "—"}</strong>
                    <span className="text-white/35 ml-2">· {new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.note && <div className="text-[12.5px] text-white/65 mt-2 italic bg-black/20 border border-white/5 rounded-lg p-3">"{r.note}"</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.filter((s) => s !== r.status).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(r.id, s)}
                      data-testid={`mark-referral-${s}-${r.id}`}
                      className={`text-[11px] uppercase tracking-[0.1em] font-medium px-3 py-1.5 rounded-md border transition-colors ${STATUS_TINT[s]} hover:brightness-125`}
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] uppercase tracking-[0.1em] font-medium px-3 py-1.5 rounded-full border transition-colors capitalize ${active ? "bg-[#FFB000]/15 text-[#FFB000] border-[#FFB000]/40" : "text-white/55 border-white/10 hover:text-white"}`}
    >
      {children}
    </button>
  );
}
